/**
 * AI-Enhanced Deal Analysis Prompt for Claude
 * Generates professional investment insights for deal comp analysis emails
 */

export interface DealAnalysisAIRequest {
  propertyAddress: string;
  listPrice: number;
  arv: number;
  renoEstimate: number;
  roi: number;
  bedrooms?: number;
  bathrooms?: number;
  sqft?: number;
  yearBuilt?: number;
  daysOnMarket?: number;
  renovatedComps: any[];
  asIsComps: any[];
  maoGapPct?: number;
  investmentGrade?: string;
  clientBudget?: number;
  targetResale?: number;
}

export function generateDealAnalysisPrompt(data: DealAnalysisAIRequest): string {
  const conservativeARV = data.renovatedComps && data.renovatedComps.length > 0
    ? calculateConservativeARV(data.renovatedComps)
    : data.arv;

  return `You are a professional real estate investment analyst. Analyze this investment property and provide strategic insights.

PROPERTY DETAILS:
- Address: ${data.propertyAddress}
- List Price: $${data.listPrice.toLocaleString()}
- Configuration: ${data.bedrooms || '?'}BR/${data.bathrooms || '?'}BA, ${data.sqft?.toLocaleString() || '?'} sqft
- Year Built: ${data.yearBuilt || 'Unknown'}
- Days on Market: ${data.daysOnMarket || 'New'}

FINANCIAL METRICS:
- After Repair Value (ARV): $${data.arv.toLocaleString()}
- Conservative ARV: $${conservativeARV.toLocaleString()}
- Renovation Budget: $${data.renoEstimate.toLocaleString()}
- ROI: ${data.roi}%
- Investment Grade: ${data.investmentGrade || 'B'}
- MAO Position: ${data.maoGapPct ? `${data.maoGapPct}% ${data.maoGapPct > 0 ? 'above' : 'below'} MAO` : 'At MAO'}

COMPARABLE ANALYSIS:
- Renovated Comps: ${data.renovatedComps?.length || 0} properties
- Median Renovated Sale: $${calculateMedianPrice(data.renovatedComps)}
- As-Is Comps: ${data.asIsComps?.length || 0} properties

CLIENT PARAMETERS:
- Target Renovation Budget: $${(data.clientBudget || 30000).toLocaleString()}
- Target Resale Price: $${(data.targetResale || conservativeARV).toLocaleString()}

Please provide:
1. **Market Position Analysis** (2-3 sentences): How does this property compare to recent sales? Is it priced appropriately?

2. **Investment Strengths** (2-3 bullet points): What makes this a good investment?

3. **Risk Factors** (2-3 bullet points): What should the investor be cautious about?

4. **Renovation Strategy** (2-3 sentences): How should they allocate the $${(data.clientBudget || 30000).toLocaleString()} budget for maximum ROI?

5. **Negotiation Tactics** (2-3 sentences): How should they approach the seller? What's the ideal offer strategy?

6. **Exit Strategy** (1-2 sentences): Timeline and pricing strategy for resale.

Format your response in clear sections with headers. Be specific with numbers and percentages. Focus on actionable insights.`;
}

function calculateConservativeARV(comps: any[]): number {
  if (!comps || comps.length === 0) return 0;
  
  const prices = comps
    .map(comp => comp.close_price || comp.sold_price || comp.sale_price || 0)
    .filter(price => price > 0)
    .sort((a, b) => a - b);
  
  if (prices.length === 0) return 0;
  
  // Take bottom 3 or all if less than 3
  const bottomPrices = prices.slice(0, Math.min(3, prices.length));
  const avgPrice = bottomPrices.reduce((sum, price) => sum + price, 0) / bottomPrices.length;
  
  return Math.round(avgPrice / 5000) * 5000;
}

function calculateMedianPrice(comps: any[]): string {
  if (!comps || comps.length === 0) return '0';
  
  const prices = comps
    .map(comp => comp.close_price || comp.sold_price || comp.sale_price || 0)
    .filter(price => price > 0)
    .sort((a, b) => a - b);
  
  if (prices.length === 0) return '0';
  
  const mid = Math.floor(prices.length / 2);
  const median = prices.length % 2 !== 0
    ? prices[mid]
    : (prices[mid - 1] + prices[mid]) / 2;
  
  return median.toLocaleString();
}

/**
 * Call the AI service to get enhanced analysis
 */
export async function getAIEnhancedAnalysis(
  dealData: any,
  axiosInstance: any
): Promise<string> {
  try {
    const prompt = generateDealAnalysisPrompt({
      propertyAddress: dealData.unparsed_address || dealData.address,
      listPrice: dealData.list_price,
      arv: dealData.renovated_arv || dealData.estimated_arv,
      renoEstimate: dealData.reno_estimate || dealData.estimated_repair_cost || 30000,
      roi: dealData.flip_roi,
      bedrooms: dealData.bedrooms,
      bathrooms: dealData.bathrooms,
      sqft: dealData.living_area,
      yearBuilt: dealData.year_built,
      daysOnMarket: dealData.days_on_market,
      renovatedComps: dealData.top_renovated_comps || [],
      asIsComps: dealData.top_as_is_comps || [],
      maoGapPct: dealData.mao_gap_pct,
      investmentGrade: dealData.flip_grade || dealData.deal_grade,
      clientBudget: 30000,
      targetResale: dealData.renovated_arv || dealData.estimated_arv || 500000
    });

    console.log('Sending AI analysis request for deal:', dealData.id);

    // Use the correct AI analysis endpoint
    const response = await axiosInstance.post(`/lead/opportunities/${dealData.id}/analyze`, {
      prompt: prompt,
      dealData: {
        propertyAddress: dealData.unparsed_address || dealData.address,
        listPrice: dealData.list_price,
        arv: dealData.renovated_arv || dealData.estimated_arv,
        renoEstimate: dealData.reno_estimate || dealData.estimated_repair_cost || 30000,
        roi: dealData.flip_roi,
        bedrooms: dealData.bedrooms,
        bathrooms: dealData.bathrooms,
        sqft: dealData.living_area,
        yearBuilt: dealData.year_built
      },
      context: {
        type: 'investment_analysis',
        deal_id: dealData.id
      }
    });

    console.log('AI response received:', response.data);

    if (response.data && response.data.response) {
      return formatAIResponse(response.data.response);
    } else if (response.data && response.data.message) {
      return formatAIResponse(response.data.message);
    }
    
    return '';
  } catch (error) {
    console.error('Error getting AI analysis:', error);
    // Return a fallback message
    return `
    <div style="background: #fff5f5; border-radius: 8px; padding: 20px; margin: 20px 0; border: 1px solid #feb2b2;">
      <h3 style="margin: 0 0 10px; color: #c53030; font-size: 18px;">
        ⚠️ AI Analysis Temporarily Unavailable
      </h3>
      <p style="color: #742a2a; margin: 0;">
        We couldn't generate the AI analysis at this time. The detailed comp analysis above provides comprehensive investment metrics.
      </p>
    </div>
    `;
  }
}

function formatAIResponse(aiText: string): string {
  // Convert the AI response to HTML format for email
  // First, split into sections based on headers (lines starting with numbers and bold text)
  const sections = aiText.split(/(?=\d+\.\s*\*\*)/);
  
  let formattedHtml = `
<div style="background: linear-gradient(135deg, #f8fafc 0%, #e6fffa 100%); border-radius: 12px; padding: 30px; margin: 30px 0; border: 1px solid #38b2ac;">
  <h3 style="margin: 0 0 25px; color: #065666; font-size: 24px; text-align: center;">
    KairosIQ AI Realty Powered Investment Analysis
  </h3>
  
  <div style="color: #2d3748; line-height: 1.8;">
`;

  sections.forEach(section => {
    if (section.trim()) {
      // Extract section header
      const headerMatch = section.match(/^(\d+)\.\s*\*\*(.+?)\*\*/);
      if (headerMatch) {
        const sectionNumber = headerMatch[1];
        const sectionTitle = headerMatch[2];
        const sectionContent = section.substring(headerMatch[0].length).trim();
        
        formattedHtml += `
    <div style="margin-bottom: 25px;">
      <h4 style="color: #065666; font-size: 18px; margin: 0 0 10px; border-bottom: 2px solid #38b2ac; padding-bottom: 5px;">
        ${sectionNumber}. ${sectionTitle}
      </h4>
      <div style="color: #4a5568; padding-left: 10px;">
        ${formatContent(sectionContent)}
      </div>
    </div>
`;
      } else {
        // Handle content without clear header
        formattedHtml += `<div style="margin-bottom: 15px;">${formatContent(section)}</div>`;
      }
    }
  });

  formattedHtml += `
  </div>
  
  <div style="margin-top: 25px; padding-top: 20px; border-top: 1px solid #cbd5e0; text-align: center;">
    <p style="color: #718096; font-size: 12px; margin: 0;">
      This AI analysis is based on current market data and comparable sales. Market conditions are subject to change and this analysis is for investment evaluation purposes.
    </p>
  </div>
</div>
`;

  return formattedHtml;
}

function formatContent(content: string): string {
  return content
    // Convert markdown bold to HTML strong
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color: #2d3748;">$1</strong>')
    // Convert bullet points to styled lists
    .replace(/^[\-\•]\s+(.+)$/gm, '<li style="margin: 5px 0;">$1</li>')
    // Wrap consecutive list items in ul tags
    .replace(/(<li.*?<\/li>\s*)+/g, '<ul style="margin: 10px 0; padding-left: 20px;">$&</ul>')
    // Convert line breaks to paragraphs
    .replace(/\n\n/g, '</p><p style="margin: 10px 0;">')
    // Ensure content is wrapped in paragraph tags
    .replace(/^(?!<)/, '<p style="margin: 10px 0;">')
    .replace(/(?<!>)$/, '</p>')
    // Style percentages and numbers
    .replace(/(\d+(?:\.\d+)?%)/g, '<span style="font-weight: 600; color: #065666;">$1</span>')
    .replace(/(\$[\d,]+(?:\.\d+)?)/g, '<span style="font-weight: 600; color: #065666;">$1</span>');
}